// Mini modal with picture having a gray background around it (two "blocks")
import React, { useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import _ from 'lodash';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type RestModalEmailStructuredModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import FunnelCheckbox from '@bbkFunnel/components/checkbox/funnel-checkbox';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import {
  FunnelButton,
  FunnelButtonIcon,
  FunnelTextArea,
  getSvgIcon,
} from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestModalEmailStructuredModal;
  close: () => void;
  handleAction: (
    forwards: RestModalForwards,
    { message }: { message: string }
  ) => void;
};

const RenderStructuredContent: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      dismissable,
      requires: {
        forwards,
        info: { title, placeholder, optionsExplanation, options, allowOther },
      },
      mobileLayout,
    },
    close,
    handleAction,
  } = props;
  const { mobileView, tabletView } = useMediaQueries();
  const [messageContent, setMessageContent] = useState('');
  const [otherActive, setOtherActive] = useState(false);
  const theme = useTheme() as EmotionTheme;

  const { previewCanvas } = useCardRenderEnvironment();
  const isPreview = !!previewCanvas;

  const handleForwards = () => {
    console.log(messageContent);
    // verified by a active or disabled button
    handleAction(
      { ...forwards, sync: { ...forwards.sync, message: messageContent } },
      { message: messageContent }
    );
  };

  const Icon = forwards.icon ? (
    <FunnelButtonIcon>{getSvgIcon(forwards.icon)}</FunnelButtonIcon>
  ) : null;

  const color = theme.modalTextColor || theme.textColor;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const isMobilePreview = previewCanvas?.device === 'mobile';

  const actionButton = (
    <FunnelButton
      onClick={handleForwards}
      specificStyles={{ width: '100%' }}
      classes="d-flex align-items-center justify-content-center"
      disabled={messageContent === ''}
      data-part="primary-action"
    >
      {Icon}
      <span style={{ paddingLeft: '5px' }}>{forwards.text}</span>
    </FunnelButton>
  );

  const content = (
    <ModalDesktopInnerSpacer
      css={css`
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${tabletView && !isPreview && 'width: 450px;'}
      `}
      className={cs('email-structured', 'display-grid', 'inner-spacer', {
        mobile: mobileView,
      })}
    >
      <OfferTitleMobile
        data-part="header"
        title={<TitleTheme title={title} data-part="title" />}
        close={close}
        dismissable={dismissable}
      />
      <div css={{ marginBottom: '1rem' }}>
        <div css={{ marginBottom: '1rem' }} data-part="description">
          <CustomerHtml value={optionsExplanation} />
        </div>
        <div data-part="options">
          {options.map((opt, i) => (
            <FunnelCheckbox
              key={i}
              label={opt}
              name={_.kebabCase(opt)}
              wrapperStyles={{ marginBottom: '.3rem' }}
              checked={messageContent === opt && messageContent !== ''}
              handleChange={() => {
                setOtherActive(false);
                setMessageContent(opt === messageContent ? '' : opt);
              }}
              modal
            />
          ))}
          {allowOther && (
            <>
              <FunnelCheckbox
                label={theme.dictionaryOther ?? 'Other'}
                name="otherStructured"
                wrapperStyles={{ marginBottom: '1rem' }}
                checked={otherActive}
                handleChange={() => {
                  setOtherActive(!otherActive);
                  setMessageContent('');
                }}
                modal
              />
              {otherActive && (
                <FunnelTextArea
                  name="feedback"
                  value={messageContent}
                  rows={2}
                  placeholder={placeholder}
                  onChange={(e) => setMessageContent(e.target.value)}
                  onBlur={(e) => setMessageContent(e.target.value)}
                  additionalStyles={{
                    color,
                    border: `2px solid ${new TinyColor(color)
                      .setAlpha(0.4)
                      .toHexString()}`,
                  }}
                  maxLength={240}
                  modalTextColor
                />
              )}
            </>
          )}
        </div>
      </div>
      {equalButtonSize && (mobileView || isMobilePreview) ? (
        <div className="tw-px-[11px]">{actionButton}</div>
      ) : (
        actionButton
      )}
    </ModalDesktopInnerSpacer>
  );

  if (previewCanvas?.device === 'mobile') {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {content}
      </MobileDevicePreview>
    );
  }

  return content;
};

export const StructuredContent = Object.assign(RenderStructuredContent, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'text',
      selector: '[data-part="title"]',
      placement: 'right',
    },
    header: {
      path: ['dismissable'],
      control: 'boolean',
      selector: '[data-part="header"]',
      placement: 'top-end',
      label: 'Show dismiss action',
      options: {
        defaultValue: true,
      },
    },
    description: {
      path: ['requires', 'info', 'optionsExplanation'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    options: {
      path: ['requires', 'info'],
      control: 'group',
      selector: '[data-part="options"]',
      placement: 'left',
      fields: [
        {
          path: ['options'],
          control: 'textList',
          label: 'Options',
        },
        {
          path: ['allowOther'],
          control: 'boolean',
          label: 'Allow free-form response entry',
        },
        {
          path: ['placeholder'],
          control: 'text',
          label: 'Placeholder text in other text input',
        },
      ],
    },
    action: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
      options: {
        message: false,
      },
    },
  } satisfies InlineEditorHints,
});
